import { Controller } from "@hotwired/stimulus";
import {
    AREA_SELECT_ID,
    BUILDING_SELECT_ID,
    REGION_SELECT_ID,
    ROOM_SELECT_ID
} from "constants/device_form_constants";
import { getAdjacentSelectValue } from "shared/selects/select_helpers";
import { REPORT_TABLE_ID } from "constants/print_mapping_constants";
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications";
import { getCsrfToken } from "shared/form_helpers";
import { getPrintMappingReports } from "shared/print_mapping/reports";

// Connects to data-controller="print-mapping-export"
export default class extends Controller {

    disconnect() {
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
        }
    }

    /**
     * Get the argus--table controller for the print mapping export table
     */
    _getArgusTable() {
        const el = document.getElementById(REPORT_TABLE_ID);
        if (!el || !window.Stimulus) return null;
        return window.Stimulus.getControllerForElementAndIdentifier(el, 'argus--table');
    }

    /**
     * Refresh the table by re-fetching reports
     */
    _refreshTable() {
        const argusTable = this._getArgusTable();
        if (!argusTable) return;

        getPrintMappingReports().then((results) => {
            const formatted = formatTableData(results);
            argusTable.loadData(formatted);
        });
    }

    async startExport(event) {
        event.preventDefault();

        const reportName = $('#name').val();
        const regionId = getAdjacentSelectValue(REGION_SELECT_ID);
        const buildingId = getAdjacentSelectValue(BUILDING_SELECT_ID);
        const areaId = getAdjacentSelectValue(AREA_SELECT_ID);
        const roomId = getAdjacentSelectValue(ROOM_SELECT_ID);

        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': getCsrfToken()
        };

        try {
            // Step 1: Create an ephemeral ReportDefinition
            const definitionResponse = await fetch('/api/reports/report_definitions', {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    data_source: 'print_mapping',
                    name: reportName,
                    filters: {
                        region: regionId || null,
                        building: buildingId || null,
                        area: areaId || null,
                        room: roomId || null
                    }
                })
            });

            if (!definitionResponse.ok) {
                const error = await definitionResponse.json();
                sendFailureNotification(error?.message || 'Failed to start export');
                return;
            }

            const definitionJson = await definitionResponse.json();
            const reportDefinitionId = definitionJson?.data?.id;

            // Step 2: Generate a report from the ephemeral definition
            const generateResponse = await fetch('/api/reports/generate', {
                method: 'POST',
                headers,
                body: JSON.stringify({ report_definition_id: reportDefinitionId })
            });

            if (!generateResponse.ok) {
                const error = await generateResponse.json();
                sendFailureNotification(error?.message || 'Failed to start export');
                return;
            }

            const generateJson = await generateResponse.json();
            const reportId = generateJson?.data?.id;
            const reportNameFromResponse = generateJson?.data?.name || reportName;

            // Refresh the table
            this._refreshTable();

            // Clear form
            document.querySelectorAll('.tomselected').forEach(select => {
                if (select.tomselect) {
                    select.tomselect.clear();
                }
            });
            document.querySelectorAll('#name').forEach(input => {
                input.value = '';
            });

            // Step 3: Poll for completion
            this._pollForCompletion(reportId, reportNameFromResponse);
        } catch (error) {
            sendFailureNotification(error.message);
        }
    }

    _pollForCompletion(reportId, reportName) {
        if (this._pollTimer) {
            clearInterval(this._pollTimer);
        }

        this._pollTimer = setInterval(async () => {
            try {
                const response = await fetch(`/api/reports/${reportId}`, {
                    headers: { 'Accept': 'application/json', 'X-CSRF-Token': getCsrfToken() }
                });

                if (!response.ok) {
                    return;
                }

                const json = await response.json();
                const state = json?.data?.state;

                if (state === 'completed') {
                    clearInterval(this._pollTimer);
                    this._pollTimer = null;
                    this._refreshTable();
                    sendSuccessNotification(`Export ready: ${reportName}`);
                } else if (state === 'failed' || state === 'cancelled') {
                    clearInterval(this._pollTimer);
                    this._pollTimer = null;
                    sendFailureNotification(`Export failed: ${reportName}`);
                }
            } catch (error) {
                // Silently continue polling on network errors
            }
        }, 2000);
    }

}
